import { compareText, type ManualAccountTableRow } from './manualBanks.utils'

/**
 * Client-side sorting for the manual-bank tables.
 *
 * The tables hold their sort in component state (not in column prefs or the
 * URL), so leaving the page and coming back restores the default sort.
 */

/** One column's sort, in DataTable's `sorting.sortState` shape. */
export interface TableSort {
  columnKey: string
  direction: 'asc' | 'desc'
}

/** What DataTable reports on a header click; `null` means "sorting switched off". */
type DataTableSortChange = Array<{ columnKey: string; direction: 'asc' | 'desc' | null }>

/**
 * Turn DataTable's three-step header cycle (asc → desc → off) into the
 * two-step one the screens specify: a click sorts ascending, the next one
 * descending, the next ascending again. A table is never left unsorted.
 *
 * @param current - The sort before the click
 * @param next - What DataTable proposes
 * @returns The sort to apply
 */
export function twoWaySort(current: readonly TableSort[], next: DataTableSortChange): TableSort[] {
  const active = next.find((sort) => sort.direction !== null)
  if (active && active.direction) return [{ columnKey: active.columnKey, direction: active.direction }]
  // Switched off: that can only be the column that was descending.
  const previous = current[0]
  return previous ? [{ columnKey: previous.columnKey, direction: 'asc' }] : []
}

/** Signed comparison helper: `cmp` for ascending, negated for descending. */
export function signed(direction: 'asc' | 'desc', cmp: number): number {
  return direction === 'asc' ? cmp : -cmp
}

/** Comparator for one text field of an account row. */
export function byText(pick: (row: ManualAccountTableRow) => string) {
  return (a: ManualAccountTableRow, b: ManualAccountTableRow, direction: 'asc' | 'desc') =>
    signed(direction, compareText(pick(a), pick(b)))
}

/**
 * Facility order, and within one facility the account number ascending — the
 * detail table's default sort. The tie-break does not flip with the facility
 * direction: inside a facility the numbers always read upwards.
 */
export function byFacilityThenNumber(
  a: ManualAccountTableRow,
  b: ManualAccountTableRow,
  direction: 'asc' | 'desc',
): number {
  const facility = compareText(a.facilityName, b.facilityName)
  if (facility !== 0) return signed(direction, facility)
  return compareText(a.account.account_number ?? '', b.account.account_number ?? '')
}

/**
 * Accounts in Cash Position first when ascending, as the prototype sorts the
 * column; within each group by name, which does not flip with the direction.
 */
export function byCashPosition(
  a: ManualAccountTableRow,
  b: ManualAccountTableRow,
  direction: 'asc' | 'desc',
): number {
  if (a.inCashPosition !== b.inCashPosition) return signed(direction, a.inCashPosition ? -1 : 1)
  return compareText(a.account.name, b.account.name)
}
